import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone } from 'lucide-react';

/**
 * Header - TasteSkill Premium Redesign
 * 
 * Verbesserungen:
 * - Einheitliche Navigation gemäß Master-Prompt
 * - Bessere Mobile Navigation
 * - Professionelleres Erscheinungsbild
 */

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Startseite' },
    { path: '/marktplatz', label: 'GmbH kaufen' },
    { path: '/seller-onboarding', label: 'GmbH verkaufen' },
    { path: '/onboarding', label: 'GmbH gründen' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        isScrolled ? 'bg-white shadow-sm border-b border-gray-100' : 'bg-white'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="/firmbay-logo.png" 
              alt="Firmbay Logo" 
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                  isActive(link.path)
                    ? 'text-[#009E9A] bg-[#E9F7F6]'
                    : 'text-[#0B1930] hover:text-[#009E9A] hover:bg-[#E9F7F6]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            <a href="tel:+491707967959" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#009E9A] transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">0170-7967959</span>
            </a>
            <Link to="/kontakt">
              <Button className="bg-[#0B1930] hover:bg-[#162844] text-white text-sm font-semibold px-5 py-2 rounded-lg">
                Kontakt
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[#0B1930] hover:text-[#009E9A] transition-colors rounded-lg hover:bg-[#E9F7F6]"
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 text-sm font-medium transition-colors rounded-lg ${
                  isActive(link.path)
                    ? 'text-[#009E9A] bg-[#E9F7F6]'
                    : 'text-[#0B1930] hover:text-[#009E9A] hover:bg-[#E9F7F6]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Mobile Contact Info */}
            <div className="pt-4 mt-4 border-t border-gray-100 space-y-3">
              <a 
                href="tel:+491707967959" 
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600"
              >
                <Phone className="w-4 h-4 text-[#009E9A]" />
                0170-7967959
              </a>
              <Link to="/kontakt" className="block px-4">
                <Button className="w-full bg-[#0B1930] hover:bg-[#162844] text-white text-sm font-semibold rounded-lg">
                  Kontakt aufnehmen
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
